import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Check, Plus, AlertCircle, Loader2 } from 'lucide-react';
import useApi from '../../api';
import { HubspotFieldSelect } from "./HubspotFieldSelect";
import { HubspotDefaultField } from "./HubspotDefaultField";
import { CrmValueMapping } from "./CrmValueMapping";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import HubspotIcon from '../../icons/hubspot_logo.webp';
import { VisitorMappingSections } from './VisitorMappingSections';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CrmReadFieldsSection, CrmReadField } from './CrmReadFieldsSection';
import { crmEnabledAiTools, crmNormalizedActivityTypes } from '../tabs/crmTools';

interface HubspotField {
    name: string;
    label: string;
    type: string;
    description: string;
    groupName: string;
    required: boolean;
    fieldType?: string;
    externalOptions?: boolean;
    options?: Array<{ value: string; label: string }>;
}

interface HubspotFieldsModalProps {
    isOpen: boolean;
    closeModal: () => void;
    formFields: any[];
    onSave: (
        fieldMappings: Record<string, string>,
        defaultFields: Record<string, string>,
        fieldMetadata?: Record<string, { type: string; fieldType?: string; externalOptions?: boolean }>,
        readFields?: CrmReadField[],
        readDeals?: boolean,
        valueMappings?: Record<string, Record<string, string>>,
        aiTools?: string[],
        activityTypes?: string[]
    ) => void;
    existingMappings?: Record<string, string>;
    defaultFields?: Record<string, string>;
    existingValueMappings?: Record<string, Record<string, string>>;
    existingReadFields?: CrmReadField[];
    existingReadDeals?: boolean;
    existingAiTools?: string[] | null;
    existingActivityTypes?: string[] | null;
    integrationId: string;
}

export default function HubspotFieldsModal({
    isOpen,
    closeModal,
    formFields,
    onSave,
    existingMappings = {},
    defaultFields = {},
    existingValueMappings = {},
    existingReadFields = [],
    existingReadDeals = false,
    existingAiTools = null,
    existingActivityTypes = null,
    integrationId
}: HubspotFieldsModalProps) {
    const { get } = useApi();
    const [fieldMappings, setFieldMappings] = useState<Record<string, string>>(existingMappings);
    const [valueMappings, setValueMappings] = useState<Record<string, Record<string, string>>>(existingValueMappings);
    const [selectedField, setSelectedField] = useState<string | null>(null);
    const [hubspotFields, setHubspotFields] = useState<HubspotField[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [defaultFieldMappings, setDefaultFieldMappings] = useState<Record<string, string>>(defaultFields);
    const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
    const [readFields, setReadFields] = useState<CrmReadField[]>(existingReadFields);
    // Explicit saved ai_tools wins; legacy configs seed from read_fields/read_deals.
    const seedAiTools = () =>
        existingAiTools ??
        Array.from(crmEnabledAiTools({
            name: 'hubspot',
            read_fields: existingReadFields,
            read_deals: existingReadDeals,
        }));
    const [aiTools, setAiTools] = useState<string[]>(seedAiTools);
    const seedActivityTypes = () =>
        crmNormalizedActivityTypes({ activity_types: existingActivityTypes ?? undefined });
    const [activityTypes, setActivityTypes] = useState<string[]>(seedActivityTypes);

    useEffect(() => {
        if (isOpen) {
            setFieldMappings(existingMappings);
            setValueMappings(existingValueMappings);
            setDefaultFieldMappings(defaultFields);
            setReadFields(existingReadFields);
            setAiTools(seedAiTools());
            setActivityTypes(seedActivityTypes());
            setExpandedSections({});
            fetchHubspotFields();
        }
        // Only initialize from saved props when the modal opens or the integration changes.
        // Parent re-renders can create fresh mapping objects and should not reset in-progress edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, integrationId]);

    const fetchHubspotFields = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await get(`/integrations/${integrationId}/contact-fields`);
            if (response.data.status === 'success') {
                setHubspotFields(response.data.fields);
            } else {
                setError('Failed to load HubSpot fields');
            }
        } catch (err) {
            setError('Failed to load HubSpot fields');
            console.error('Error fetching HubSpot fields:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldSelect = (fieldName: string) => {
        setSelectedField(fieldName);
    };

    const CHOICE_FIELD_TYPES = ['select', 'dropdown', 'radio', 'multiselect'];

    const findFormFieldByMappingKey = (mappingKey: string) => {
        const name = mappingKey.startsWith('form.') ? mappingKey.slice('form.'.length) : mappingKey;
        return (formFields || []).find((f: any) => f?.name === name);
    };

    const supportsValueMapping = (mappingKey: string, hubspotFieldName?: string) => {
        const formField = findFormFieldByMappingKey(mappingKey);
        const hubspotField = hubspotFields.find(f => f.name === hubspotFieldName);
        return Boolean(
            formField &&
            CHOICE_FIELD_TYPES.includes(formField.type) &&
            Array.isArray(formField.options) && formField.options.length > 0 &&
            hubspotField?.type === 'enumeration' &&
            Array.isArray(hubspotField.options) && hubspotField.options.length > 0
        );
    };

    const clearValueMapping = (fieldName: string) => {
        setValueMappings(prev => {
            if (!prev[fieldName]) return prev;
            const next = { ...prev };
            delete next[fieldName];
            return next;
        });
    };

    const handleMappingUpdate = (fieldName: string, hubspotFieldName: string) => {
        const previousTarget = fieldMappings[fieldName];
        setFieldMappings(prev => ({
            ...prev,
            [fieldName]: hubspotFieldName
        }));
        // Value mappings target a specific property's options; drop them when the target changes.
        if (previousTarget && previousTarget !== hubspotFieldName) {
            clearValueMapping(fieldName);
        }
        // Keep the row open when the new target supports option-level value mapping.
        if (!supportsValueMapping(fieldName, hubspotFieldName)) {
            setSelectedField(null);
        }
    };

    const handleValueMappingChange = (fieldName: string, next: Record<string, string>) => {
        setValueMappings(prev => {
            if (Object.keys(next).length === 0) {
                if (!prev[fieldName]) return prev;
                const updated = { ...prev };
                delete updated[fieldName];
                return updated;
            }
            return { ...prev, [fieldName]: next };
        });
    };

    const handleClearMapping = (fieldName: string) => {
        const newMappings = { ...fieldMappings };
        delete newMappings[fieldName];
        setFieldMappings(newMappings);
        clearValueMapping(fieldName);
    };

    const handleCancel = () => {
        setFieldMappings(existingMappings);
        setValueMappings(existingValueMappings);
        setSelectedField(null);
        closeModal();
    };

    const cleanDefaultFields = (defaults: Record<string, string>) => {
        const next = { ...(defaults || {}) };
        delete next[""];
        Object.keys(next).forEach((k) => {
            if (!k) delete next[k];
        });
        return next;
    };

    const cleanReadFields = () =>
        readFields.filter(field => field.property && field.key);

    const cleanValueMappings = () => {
        const cleaned: Record<string, Record<string, string>> = {};
        Object.entries(valueMappings || {}).forEach(([mappingKey, optionMap]) => {
            const target = fieldMappings[mappingKey];
            if (!target) return;
            const hubspotField = hubspotFields.find(f => f.name === target);
            if (!hubspotField || hubspotField.type !== 'enumeration') return;
            const entries = Object.entries(optionMap || {}).filter(
                ([option, value]) => option && typeof value === 'string' && value.trim()
            );
            if (entries.length === 0) return;
            cleaned[mappingKey] = Object.fromEntries(entries);
        });
        return cleaned;
    };

    const handleSaveAll = () => {
        const cleanedDefaultFields = cleanDefaultFields(defaultFieldMappings);
        const cleanedReadFields = cleanReadFields();
        if (
            Object.keys(fieldMappings).length === 0 &&
            Object.keys(cleanedDefaultFields).length === 0 &&
            cleanedReadFields.length === 0 &&
            aiTools.length === 0
        ) {
            return;
        }
        const mappedHubspotFields = new Set([
            ...Object.values(fieldMappings || {}),
            ...Object.keys(cleanedDefaultFields),
        ].filter(Boolean));
        const fieldMetadata = Object.fromEntries(
            hubspotFields
                .filter(field => mappedHubspotFields.has(field.name))
                .map(field => [
                    field.name,
                    {
                        type: field.type,
                        fieldType: field.fieldType,
                        externalOptions: field.externalOptions,
                    },
                ])
        );
        // read_deals mirrors the deals tool for one release of back-compat.
        onSave(
            fieldMappings,
            cleanedDefaultFields,
            fieldMetadata,
            cleanedReadFields,
            aiTools.includes('deals'),
            cleanValueMappings(),
            aiTools,
            activityTypes
        );
        closeModal();
    };

    const getHubspotFieldLabel = (fieldName: string) => {
        const field = hubspotFields.find(f => f.name === fieldName);
        return field ? field.label : fieldName;
    };

    const isEditing = Object.keys(existingMappings).length > 0;

    const handleDefaultFieldUpdate = (hubspotField: string, value: string) => {
        setDefaultFieldMappings(prev => {
            const newMappings = { ...prev };
            delete newMappings[""];
            newMappings[hubspotField] = value;
            return newMappings;
        });
    };

    const handleRemoveDefaultField = (hubspotField: string) => {
        const newMappings = { ...defaultFieldMappings };
        delete newMappings[hubspotField];
        setDefaultFieldMappings(newMappings);
    };

    const handleAddDefaultField = () => {
        setDefaultFieldMappings(prev => ({
            ...prev,
            "": ""
        }));
    };

    const toggleSection = (sectionKey: string) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    const getMappedCount = () => Object.keys(fieldMappings).length;
    const getDefaultCount = () => Object.keys(cleanDefaultFields(defaultFieldMappings)).length;

    const hasAnyConfiguration = () => {
        return getMappedCount() > 0 || getDefaultCount() > 0 || cleanReadFields().length > 0 || aiTools.length > 0;
    };

    if (error && !isLoading) {
        return (
            <Dialog open={isOpen} onOpenChange={closeModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="h-10 w-10 rounded-lg bg-red-500/15 flex items-center justify-center">
                                <AlertCircle className="h-5 w-5 text-red-500" />
                            </div>
                            <DialogTitle>Connection Error</DialogTitle>
                        </div>
                        <DialogDescription className="text-destructive">
                            {error}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeModal}>Close</Button>
                        <Button onClick={fetchHubspotFields}>Retry</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={closeModal}>
            <DialogContent className="max-w-3xl h-[85vh] flex flex-col overflow-hidden p-0">
                {/* Header */}
                <div className="px-6 py-5 border-b bg-muted/30">
                    <DialogHeader className="space-y-3">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-background border shadow-sm flex items-center justify-center">
                                <img src={HubspotIcon} alt="HubSpot" className="h-6" />
                            </div>
                            <div>
                                <DialogTitle className="text-xl">HubSpot Contact Sync</DialogTitle>
                                <DialogDescription className="text-sm mt-0.5">
                                    Map your form fields to HubSpot contact properties
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                </div>

                <Tabs defaultValue="sync" className="flex-1 flex flex-col overflow-hidden">
                <div className="px-6 pt-3 border-b">
                    <TabsList className="mb-3">
                        <TabsTrigger value="sync">Sync to HubSpot</TabsTrigger>
                        <TabsTrigger value="read">AI Tools</TabsTrigger>
                    </TabsList>
                </div>
                <ScrollArea className="flex-1">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                            <Loader2 className="h-8 w-8 animate-spin mb-3" />
                            <span className="text-sm">Loading HubSpot fields...</span>
                        </div>
                    ) : (
                        <>
                        <TabsContent value="sync" className="mt-0">
                        <div className="p-6 space-y-6">
                            {/* Status Card */}
                            <div className="rounded-xl border bg-card p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-semibold">Contact Properties</h3>
                                        <p className="text-sm text-muted-foreground mt-0.5">
                                            Map fields to sync visitor data to HubSpot
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {getMappedCount() > 0 && (
                                            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30">
                                                <Check className="h-3 w-3 mr-1" />
                                                {getMappedCount()} mapped
                                            </Badge>
                                        )}
                                        {getDefaultCount() > 0 && (
                                            <Badge variant="secondary">
                                                {getDefaultCount()} defaults
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <VisitorMappingSections
                                formFields={formFields}
                                mappings={fieldMappings}
                                selectedKey={selectedField}
                                onSelectKey={handleFieldSelect}
                                onMapKey={handleMappingUpdate}
                                onClearKey={handleClearMapping}
                                getTargetLabel={getHubspotFieldLabel}
                                targetLabel="Select HubSpot Property"
                                queryTargetLabel="HubSpot property"
                                renderTargetInput={({ mappingKey, value, onSelect }) => {
                                    const formField = findFormFieldByMappingKey(mappingKey);
                                    const hubspotField = hubspotFields.find(f => f.name === value);
                                    const showValueMapping = supportsValueMapping(mappingKey, value);
                                    return (
                                        <div className="space-y-3">
                                            <HubspotFieldSelect
                                                fields={hubspotFields}
                                                value={value}
                                                onSelect={onSelect}
                                                placeholder="Search and select..."
                                                simpleDisplay
                                            />
                                            {showValueMapping && formField && hubspotField?.options && (
                                                <CrmValueMapping
                                                    providerName="HubSpot"
                                                    fieldOptions={formField.options}
                                                    crmOptions={hubspotField.options}
                                                    mapping={valueMappings[mappingKey] || {}}
                                                    onChange={(next) => handleValueMappingChange(mappingKey, next)}
                                                />
                                            )}
                                        </div>
                                    );
                                }}
                            />

                            <div className="rounded-xl border bg-card overflow-hidden">
                                {/* Default Fields Section */}
                                <Collapsible
                                    open={expandedSections['default'] !== false}
                                    onOpenChange={() => toggleSection('default')}
                                >
                                    <CollapsibleTrigger className="flex items-center justify-between w-full p-5 hover:bg-muted/50 transition-colors">
                                        <div className="flex items-center gap-2">
                                            {expandedSections['default'] === false ? (
                                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                            )}
                                            <h4 className="font-medium">Default Values</h4>
                                            {getDefaultCount() > 0 && (
                                                <Badge variant="secondary" className="text-[10px]">
                                                    {getDefaultCount()}
                                                </Badge>
                                            )}
                                        </div>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="px-5 pb-5 space-y-3">
                                            <p className="text-xs text-muted-foreground">
                                                Set properties that should always have a specific value
                                            </p>
                                            {Object.entries(defaultFieldMappings).map(([hubspotField, value]) => (
                                                <HubspotDefaultField
                                                    key={hubspotField || 'new'}
                                                    hubspotField={hubspotField}
                                                    value={value}
                                                    onUpdate={handleDefaultFieldUpdate}
                                                    onRemove={() => handleRemoveDefaultField(hubspotField)}
                                                    availableFields={hubspotFields}
                                                />
                                            ))}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={handleAddDefaultField}
                                                className="w-full border-dashed"
                                            >
                                                <Plus className="h-4 w-4 mr-2" />
                                                Add Default Field
                                            </Button>
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                            </div>
                        </div>
                        </TabsContent>
                        <TabsContent value="read" className="mt-0">
                            <div className="p-6">
                                <CrmReadFieldsSection
                                    integrationId={integrationId}
                                    provider="hubspot"
                                    readFields={readFields}
                                    setReadFields={setReadFields}
                                    aiTools={aiTools}
                                    setAiTools={setAiTools}
                                    activityTypes={activityTypes}
                                    setActivityTypes={setActivityTypes}
                                    renderPropertySelect={({ fields, value, onSelect }) => (
                                        <HubspotFieldSelect
                                            fields={fields}
                                            value={value}
                                            onSelect={onSelect}
                                            placeholder="Search and select..."
                                            simpleDisplay
                                        />
                                    )}
                                />
                            </div>
                        </TabsContent>
                        </>
                    )}
                </ScrollArea>
                </Tabs>

                {/* Footer */}
                <div className="border-t px-6 py-4 bg-muted/30 flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                        {hasAnyConfiguration() ? (
                            <span className="flex items-center gap-1.5">
                                <Check className="h-4 w-4 text-emerald-500" />
                                Ready to save
                            </span>
                        ) : (
                            <span>Map at least one field to save</span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSaveAll}
                            disabled={!hasAnyConfiguration()}
                        >
                            {isEditing ? 'Save Changes' : 'Create Mapping'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
